import { useState } from 'react';
import { Eye, Trash2 } from 'lucide-react';
import { useOperationLogs, usePurgeOperationLogs } from '../hooks/useOperationLogs.js';
import { useAuthStore } from '../store/auth.store.js';
import PageHeader from '../components/PageHeader.jsx';
import Table from '../components/Table.jsx';
import Badge from '../components/Badge.jsx';
import Button from '../components/Button.jsx';
import Modal from '../components/Modal.jsx';
import Input from '../components/Input.jsx';

// Un color por dirección del intercambio, igual en los 4 bloques siempre —
// así de un vistazo se distingue "entra" (azul/violeta) de "sale" (ámbar/verde)
// sin tener que leer el título de cada bloque.
const BLOCK_COLOR = {
  blue: 'border-blue-500 bg-blue-500/5',
  amber: 'border-amber-500 bg-amber-500/5',
  violet: 'border-violet-500 bg-violet-500/5',
  emerald: 'border-emerald-500 bg-emerald-500/5',
};

function JsonBlock({ title, value, color }) {
  return (
    <div className={`border-l-4 rounded-r-lg p-3 ${BLOCK_COLOR[color]}`}>
      <p className="text-xs font-medium text-text-secondary mb-1">{title}</p>
      {value == null ? (
        <p className="text-xs text-text-muted italic">— (no se llegó a esta etapa)</p>
      ) : (
        <pre className="input-base font-mono text-xs whitespace-pre overflow-x-auto max-h-48 overflow-y-auto">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}

const HTTP_STATUS_VARIANT = (status) => (status < 300 ? 'success' : status < 500 ? 'warning' : 'danger');

// response_to_client es { httpStatus, body } — el código HTTP importa tanto
// como el body: un 201 con status:"rejected" (rechazo de ARCA, no es error
// nuestro) es muy distinto de un 422/500 (ni siquiera llegamos a intentarlo).
function ResponseToClientBlock({ color, value }) {
  return (
    <div className={`border-l-4 rounded-r-lg p-3 ${BLOCK_COLOR[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <p className="text-xs font-medium text-text-secondary">④ Respuesta enviada a Ideafix</p>
        {value?.httpStatus != null && <Badge variant={HTTP_STATUS_VARIANT(value.httpStatus)}>HTTP {value.httpStatus}</Badge>}
      </div>
      {value == null ? (
        <p className="text-xs text-text-muted italic">— (no se llegó a esta etapa)</p>
      ) : (
        <pre className="input-base font-mono text-xs whitespace-pre overflow-x-auto max-h-48 overflow-y-auto">
          {JSON.stringify(value.body ?? value, null, 2)}
        </pre>
      )}
    </div>
  );
}

// Para operaciones que llegaron como .ini (Ideafix), raw_input es
// { raw, fields }: `raw` es el texto exacto recibido (auditable byte a
// byte), `fields` el desglose ya parseado. Para las que llegaron como JSON
// normal (POST /api/cae/request), raw_input es directamente el body plano.
function ReceivedBlock({ color, value }) {
  const isIniPayload = value != null && typeof value === 'object' && typeof value.raw === 'string';
  return (
    <div className={`border-l-4 rounded-r-lg p-3 ${BLOCK_COLOR[color]}`}>
      <p className="text-xs font-medium text-text-secondary mb-1">
        ① Recibido {isIniPayload ? '(.ini de Ideafix)' : '(POST /api/cae/request)'}
      </p>
      {value == null ? (
        <p className="text-xs text-text-muted italic">— (no se llegó a esta etapa)</p>
      ) : isIniPayload ? (
        <div className="space-y-2">
          <p className="text-xs text-text-muted">.ini crudo, tal cual llegó:</p>
          <pre className="input-base font-mono text-xs whitespace-pre overflow-x-auto max-h-40 overflow-y-auto">{value.raw}</pre>
          <p className="text-xs text-text-muted">Campos parseados:</p>
          <pre className="input-base font-mono text-xs whitespace-pre overflow-x-auto max-h-40 overflow-y-auto">{JSON.stringify(value.fields, null, 2)}</pre>
        </div>
      ) : (
        <pre className="input-base font-mono text-xs whitespace-pre overflow-x-auto max-h-48 overflow-y-auto">{JSON.stringify(value, null, 2)}</pre>
      )}
    </div>
  );
}

function LogDetailModal({ log, onClose }) {
  const arcaStatus = log ? getArcaStatus(log) : null;
  return (
    <Modal open={!!log} onClose={onClose} title="Detalle de la operación" maxWidth="max-w-2xl">
      {log && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-xs text-text-muted">
            <span>{new Date(log.created_at).toLocaleString('es-AR')}</span>
            <span>·</span>
            <span>{log.client_name}</span>
            <span>·</span>
            <Badge variant={log.environment === 'production' ? 'success' : 'warning'}>{log.environment}</Badge>
            <Badge variant={log.success ? 'success' : 'danger'}>sistema: {log.success ? 'ok' : 'error'}</Badge>
            {arcaStatus && <Badge variant={arcaStatus.variant}>ARCA: {arcaStatus.label}</Badge>}
            {log.total_response_time_ms != null && <Badge variant="neutral">Total: {log.total_response_time_ms} ms</Badge>}
            {log.arca_response_time_ms != null && <Badge variant="neutral">ARCA: {log.arca_response_time_ms} ms</Badge>}
          </div>
          {!log.success && (
            <p className="text-xs text-red-400">{log.error_code}: {log.error_message}</p>
          )}
          {Array.isArray(log.arca_calls) && log.arca_calls.length > 0 && (
            <p className="text-xs text-text-muted">
              Desglose ARCA: {log.arca_calls.map((c) => `${c.operation} ${c.elapsedMs != null ? `${c.elapsedMs} ms` : '—'}`).join(' · ')}
            </p>
          )}
          <ReceivedBlock color="blue" value={log.raw_input} />
          <JsonBlock color="amber" title="② Enviado a ARCA (FECAESolicitar)" value={log.arca_request} />
          <JsonBlock color="violet" title="③ Respuesta de ARCA" value={log.arca_response} />
          <ResponseToClientBlock color="emerald" value={log.response_to_client} />
        </div>
      )}
    </Modal>
  );
}

// `success` solo dice si NUESTRO sistema completó el ciclo sin explotar —
// un comprobante que ARCA rechazó (CUIT inválido, etc.) también es
// success:true, porque el sistema funcionó bien, solo que el resultado de
// negocio fue un rechazo. Sin esto en una columna aparte, un rechazo de
// ARCA se confunde con un envío exitoso al filtrar por "Éxito".
const ARCA_STATUS = {
  approved: { label: 'aprobado', variant: 'success' },
  approved_with_observations: { label: 'aprobado con obs.', variant: 'warning' },
  rejected: { label: 'rechazado', variant: 'danger' },
};

function getArcaStatus(log) {
  const status = log.response_to_client?.status;
  return status ? (ARCA_STATUS[status] ?? null) : null;
}

const SUCCESS_LABEL = { true: 'OK', false: 'Error' };

export default function OperationLogs() {
  const { user } = useAuthStore();
  const [environment, setEnvironment] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const filters = {};
  if (environment) filters.environment = environment;
  if (success) filters.success = success;
  const { data, isLoading } = useOperationLogs(filters);
  const purge = usePurgeOperationLogs();

  const handlePurge = async () => {
    const days = prompt('Borrar logs de más de cuántos días:', '30');
    if (!days) return;
    const n = Number(days);
    if (!Number.isFinite(n) || n < 1) return alert('Número inválido');
    if (!confirm(`¿Borrar todos los logs de operaciones de más de ${n} días? No se puede deshacer.`)) return;
    const result = await purge.mutateAsync(n);
    alert(`Se borraron ${result.deleted} entradas.`);
  };

  return (
    <div>
      <PageHeader
        title="Logs de operaciones"
        subtitle="Lo que recibimos de los ERPs (Ideafix) y lo que se intercambió con ARCA en cada pedido de CAE"
        actions={
          <div className="flex gap-2">
            <Input as="select" value={environment} onChange={(e) => setEnvironment(e.target.value)} className="w-40">
              <option value="">Todos los entornos</option>
              <option value="testing">Testing</option>
              <option value="production">Producción</option>
            </Input>
            <Input as="select" value={success} onChange={(e) => setSuccess(e.target.value)} className="w-36">
              <option value="">Todos</option>
              <option value="true">Éxito</option>
              <option value="false">Error</option>
            </Input>
            {user?.role === 'superadmin' && (
              <Button variant="danger" loading={purge.isPending} onClick={handlePurge}>
                <Trash2 size={14} /> Purgar
              </Button>
            )}
          </div>
        }
      />
      <div className="card">
        <Table
          loading={isLoading}
          data={data}
          emptyMessage="Sin operaciones registradas"
          columns={[
            { key: 'created_at', label: 'Fecha', render: (r) => new Date(r.created_at).toLocaleString('es-AR') },
            { key: 'client_name', label: 'Cliente' },
            { key: 'environment', label: 'Entorno', render: (r) => <Badge variant={r.environment === 'production' ? 'success' : 'warning'}>{r.environment}</Badge> },
            { key: 'operation', label: 'Operación' },
            { key: 'success', label: 'Sistema', render: (r) => <Badge variant={r.success ? 'success' : 'danger'}>{SUCCESS_LABEL[r.success]}</Badge> },
            {
              key: 'arca_status', label: 'Resultado ARCA', render: (r) => {
                const status = getArcaStatus(r);
                return status ? <Badge variant={status.variant}>{status.label}</Badge> : '—';
              },
            },
            {
              key: 'total_response_time_ms', label: 'Tiempo total',
              render: (r) => (r.total_response_time_ms != null ? `${r.total_response_time_ms} ms` : '—'),
            },
            {
              key: 'arca_response_time_ms', label: 'Tiempo ARCA',
              render: (r) => (r.arca_response_time_ms != null ? `${r.arca_response_time_ms} ms` : '—'),
            },
            { key: 'error_code', label: 'Error', render: (r) => r.error_code || '—' },
            {
              key: 'actions', label: '', render: (r) => (
                <button onClick={() => setSelectedLog(r)} className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-accent" title="Ver detalle">
                  <Eye size={15} />
                </button>
              ),
            },
          ]}
        />
      </div>
      <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
}
